import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";
import { useWatch, type Control, type DeepPartial } from "react-hook-form";

import type { ConfigurationFormField, ConfigurationFormValues } from "../types";

import { segmentDetailQueryOptions } from "@/features/segments/api/useSegmentDetail";

const isSegmentReference = (field: ConfigurationFormField) =>
  field.type === "reference" &&
  field.options["reference-type"].toLowerCase() === "segment";

export function collectSegmentIds(
  formFields: ConfigurationFormField[],
  values: DeepPartial<ConfigurationFormValues>,
): string[] {
  const ids = formFields.flatMap((field) => {
    const value = values[field.key];

    if (isSegmentReference(field))
      return typeof value === "string" && value ? [value] : [];

    if (field.type === "multi-input-list" && Array.isArray(value)) {
      const segmentKeys = field.options.fields
        .filter(isSegmentReference)
        .map((f) => f.key);
      return value.flatMap((item) =>
        segmentKeys
          .map((key) => item?.values?.[key])
          .filter((id): id is string => typeof id === "string" && id !== ""),
      );
    }

    return [];
  });

  return [...new Set(ids)];
}

export function useArchivedSegmentReferences(
  control: Control<ConfigurationFormValues>,
  formFields: ConfigurationFormField[] | undefined,
  clientId: string | undefined,
): string[] {
  const values = useWatch({ control });
  const segmentIds = useMemo(
    () => (formFields && clientId ? collectSegmentIds(formFields, values) : []),
    [formFields, clientId, values],
  );

  return useQueries({
    queries: segmentIds.map((segmentId) => ({
      ...segmentDetailQueryOptions({ segmentId, clientId }),
      refetchOnReconnect: false,
    })),
    combine: (results) =>
      results.flatMap((result) =>
        result.data?.archivedAt ? [result.data.name] : [],
      ),
  });
}
